import React, { useState, useEffect } from 'react';
import { Phone } from 'lucide-react';

const PhoneInput = ({ value, onChange, label, required = false, error = null, placeholder = "Enter phone number" }) => {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Fetch country codes
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/poc/country-codes`);
        if (response.ok) {
          const data = await response.json();
          setCountries(data.countries || []);
        }
      } catch (error) {
        console.error('Error fetching country codes:', error);
        // Fallback countries
        setCountries([
          {code: "US", name: "United States", dialCode: "+1"},
          {code: "CA", name: "Canada", dialCode: "+1"},
          {code: "GB", name: "United Kingdom", dialCode: "+44"},
          {code: "IN", name: "India", dialCode: "+91"}
        ]);
      }
    };
    fetchCountries();
  }, []);

  // Parse incoming value if it's in JSON format
  useEffect(() => {
    if (value) {
      if (typeof value === 'object' && value.countryCode && value.number) {
        setSelectedCountry(value.countryCode);
        setPhoneNumber(value.number);
      } else if (typeof value === 'string') {
        // Try to parse legacy format
        setPhoneNumber(value.replace(/\D/g, ''));
      }
    }
  }, [value]);

  // Notify parent component when phone changes
  useEffect(() => {
    if (phoneNumber) {
      onChange({
        countryCode: selectedCountry,
        number: phoneNumber
      });
    } else {
      onChange(null);
    }
  }, [selectedCountry, phoneNumber]);

  const handlePhoneChange = (e) => {
    // Only allow numeric input
    const numericValue = e.target.value.replace(/\D/g, '');
    
    // Apply max length based on country
    let maxLength = 15; // Default for international
    if (selectedCountry === '+1') {
      maxLength = 10; // US/Canada
    } else if (selectedCountry === '+44') {
      maxLength = 10; // UK
    } else if (selectedCountry === '+91') {
      maxLength = 10; // India
    }
    
    // Truncate if exceeds max length
    const truncated = numericValue.slice(0, maxLength);
    setPhoneNumber(truncated);
  };

  const handleKeyPress = (e) => {
    // Prevent non-numeric characters
    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      e.preventDefault();
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="flex gap-2">
        {/* Country Code Dropdown */}
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          required={required}
        >
          {countries.map(country => (
            <option key={country.code} value={country.dialCode}>
              {country.code} {country.dialCode}
            </option>
          ))}
        </select>

        {/* Phone Number Input */}
        <div className="flex-1 relative">
          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onKeyPress={handleKeyPress}
            required={required}
            placeholder={placeholder}
            className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
            inputMode="numeric"
            pattern="[0-9]*"
          />
        </div>
      </div>
      
      {/* Validation hint */}
      <p className="text-xs text-gray-500 mt-1">
        {selectedCountry === '+1' 
          ? 'Enter exactly 10 digits (e.g., 2025551234)' 
          : 'Enter number without country code (max 15 digits)'}
      </p>
      
      {/* Error message */}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      
      {/* Preview */}
      {phoneNumber && (
        <p className="text-xs text-green-600 mt-1">
          Will be saved as: {selectedCountry} {phoneNumber}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
